/**
 * Which legal entity the marketing build speaks for.
 *
 * `VITE_SITE_REGION=in` is the flogpt.in image (VDigit Solutions Private
 * Limited, Kolkata). Any other value, including unset, is flogpt.com
 * (Pulse Real LLC). The comparison is the literal `import.meta.env` access
 * Vite replaces at build time, so the India strings are dead-code-eliminated
 * from the .com bundle. Do not switch this on `window.location.hostname`:
 * a .com build must be incapable of rendering the India entity.
 */

export const GLOBAL_ORIGIN = "https://flogpt.com";
export const INDIA_ORIGIN = "https://flogpt.in";

export const isIndiaSite = (): boolean => import.meta.env.VITE_SITE_REGION === "in";

/**
 * Public origin for canonicals and JSON-LD.
 * India is pinned so prerender's localhost browser cannot bake flogpt.com.
 * The default build keeps `window.location.origin` (the prerender step
 * rewrites that localhost to `PRERENDER_ORIGIN`, which stays flogpt.com).
 */
export const marketingOrigin = (): string => {
    if (import.meta.env.VITE_SITE_REGION === "in") return INDIA_ORIGIN;
    if (typeof window === "undefined") return GLOBAL_ORIGIN;
    return window.location.origin;
};

/**
 * On the India build, an absolute flogpt.com or www.flogpt.com URL (http or
 * https) is rewritten to https://flogpt.in, keeping its path. Used for
 * canonicals, og:image, twitter:image, and JSON-LD image fields. Other hosts
 * stay put. The default build returns the value unchanged.
 */
export const localizePublicAssetUrl = (value: string): string => {
    if (import.meta.env.VITE_SITE_REGION !== "in") return value;
    if (!/^https?:\/\//i.test(value)) return value;
    try {
        const url = new URL(value);
        if (url.hostname === "flogpt.com" || url.hostname === "www.flogpt.com") {
            return `https://flogpt.in${url.pathname}${url.search}`;
        }
    } catch {
        return value;
    }
    return value;
};

/**
 * og:image and twitter:image must be absolute. Root-relative settings values
 * (`/images/og-default.jpg`) join the build origin: flogpt.in on the India
 * image, and `window.location.origin` on the default image (prerender then
 * rewrites that localhost to PRERENDER_ORIGIN, which stays flogpt.com).
 */
export const absolutePublicAssetUrl = (value: string): string => {
    if (!value) return value;
    const localized = localizePublicAssetUrl(value);
    if (/^https?:\/\//i.test(localized)) return localized;
    const path = localized.startsWith("/") ? localized : `/${localized}`;
    return `${marketingOrigin()}${path}`;
};

/** Visible support number. The India NAP replaces the legacy contact number. */
export const contactPhone = (): { display: string; tel: string } => {
    if (import.meta.env.VITE_SITE_REGION === "in") {
        return { display: "+91 9560303603", tel: "+919560303603" };
    }
    return { display: "+91 90078 55673", tel: "+919007855673" };
};
